/**
 * French language strings for the Vivatis PDB application.
 * All user-facing text should be defined here for easy localisation.
 */

const fr = {
  /* ------------------------------------------------------------------ */
  /* Global / shared                                                    */
  /* ------------------------------------------------------------------ */
  common: {
    products: 'Produits',
    product: 'produit',
    productsPlural: 'produits',
    partner: 'Partenaire',
    name: 'Nom',
    category: 'Catégorie',
    yes: 'Oui',
    no: 'Non',
    back: 'Retour',
    next: 'Suivant',
    skip: 'Ignorer',
    filter: 'Filtrer',
    filterMode: 'Mode de filtrage',
    and: 'ET',
    or: 'OU',
    clearAll: 'Tout effacer',
    removeAll: 'Tout supprimer',
    browseProducts: 'Parcourir les produits',
    noProductsFound: 'Aucun produit trouvé.',
    noResultsFor: (term: string) => `Aucun résultat pour « ${term} ».`,
    new: 'NOUVEAU',
    pdf: 'PDF',
    viewAll: 'Tout afficher',
    offlineBanner: 'Vous êtes hors ligne. Certaines fonctionnalités sont restreintes.',
    offlineTooltip: 'Non disponible en mode hors ligne',
    offlineLanguageHint: 'Hors ligne : la langue de l’interface change, les données produit restent dans la langue précédemment chargée jusqu’à votre reconnexion',
    pwaUpdateAvailable: 'Une nouvelle version est disponible. Mise à jour...',
    pwaInstallTitle: 'Installer l\'app',
    pwaInstallDescription: 'Ajouter à l\'écran d\'accueil pour un accès rapide.',
    pwaInstallButton: 'Installer',
  },

  /* ------------------------------------------------------------------ */
  /* Navigation                                                         */
  /* ------------------------------------------------------------------ */
  nav: {
    home: 'Accueil',
    products: 'Produits',
    wizard: 'Assistant',
    favorites: 'Favoris',
    compare: 'Comparer',
    cart: 'Documents',
    searchPlaceholder: 'Rechercher des produits...',
    toggleTheme: 'Changer le thème',
    logout: 'Déconnexion',
    admin: 'Admin',
    toggleMenu: 'Basculer le menu',
  },

  /* ------------------------------------------------------------------ */
  /* Branding                                                           */
  /* ------------------------------------------------------------------ */
  brand: {
    vivatis: 'VIVATIS',
    pdb: 'PDB',
    pharma: 'PHARMA',
    productDatabase: 'Base de données produits',
    copyright: 'Vivatis Pharma GmbH © 2026',
  },

  /* ------------------------------------------------------------------ */
  /* Login                                                              */
  /* ------------------------------------------------------------------ */
  login: {
    email: 'E-mail',
    password: 'Mot de passe',
    showPassword: 'Afficher le mot de passe',
    hidePassword: 'Masquer le mot de passe',
    signingIn: 'Connexion...',
    signIn: 'Se connecter',
    forgotPassword: 'Mot de passe oublié ?',
  },

  /* ------------------------------------------------------------------ */
  /* Home page                                                          */
  /* ------------------------------------------------------------------ */
  home: {
    welcomeBack: (name: string) => `Bon retour${name ? `, ${name}` : ''}\u00a0!`,
    whatToDo: 'Que souhaitez-vous faire aujourd\'hui\u00a0?',
    stats: {
      products: 'Produits',
      new: 'Nouveau',
      favorites: 'Favoris',
      inCart: 'Dans le document',
    },
    actions: {
      browseProducts: 'Parcourir les produits',
      viewAllProducts: (count: number) => (count === 1 ? 'Voir 1 produit' : `Voir tous les ${count} produits`),
      browseProductsCount: (count: number) => `${count} produits`,
      loading: 'Chargement...',
      startWizard: 'Lancer l\'assistant',
      findMatching: 'Trouver des produits correspondants',
      favorites: 'Favoris',
      productsSaved: (count: number) => `${count} enregistrés`,
      meetingCart: 'Document de réunion',
      productsInCart: (count: number) => `${count} dans le document`,
    },
    browseByIndication: 'Parcourir par indication',
    topIndications: 'Vos principales indications',
    topIndicationsEmpty: 'Commencez à parcourir les produits pour voir vos indications les plus consultées ici.',
    newProducts: 'Nouveaux produits',
    newProductsTooltip: 'Tous les produits ajoutés au cours des 12 derniers mois sont considérés comme nouveaux.',
    newStudies: 'Nouvelles études',
    newStudiesTooltip: 'Produits avec des études cliniques récemment publiées au cours des 12 derniers mois.',
    newStudyBadge: 'Nouvelle étude',
    publishedStudies: (count: number) => `${count} études cliniques publiées`,
    favoritesTitle: 'Favoris',
    noFavorites: 'Aucun favori pour l\'instant. Cliquez sur l\'icône cœur d\'un produit pour l\'enregistrer ici.',
    productCountLabel: (count: number) => `${count} ${count === 1 ? 'produit' : 'produits'}`,
    recentlyViewed: 'Récemment consultés',
    noRecentlyViewed: 'Aucun produit consulté pour l\'instant. Commencez avec le',
    productCatalog: 'Catalogue produits',
    viewAll: 'Voir tout',
  },

  /* ------------------------------------------------------------------ */
  /* Product list                                                       */
  /* ------------------------------------------------------------------ */
  productList: {
    sort: {
      relevance: 'Pertinence',
      nameAsc: 'Nom (A-Z)',
      nameDesc: 'Nom (Z-A)',
      articleNumber: 'N° d\'article',
      partner: 'Partenaire',
      indication: 'Indication',
    },
    viewTable: 'Tableau',
    viewCards: 'Cartes',
    productsFound: (count: number) => `${count} produits trouvés`,
    productCount: (count: number, total: number) => `${count} sur ${total} produits`,
    loading: 'Chargement des produits...',
    adjustFilters: 'Ajustez vos filtres.',
    resetFilters: 'Réinitialiser les filtres',
  },

  /* ------------------------------------------------------------------ */
  /* Product search                                                     */
  /* ------------------------------------------------------------------ */
  productSearch: {
    placeholder: 'Rechercher par nom, numéro d\'article, ingrédient, indication...',
  },

  /* ------------------------------------------------------------------ */
  /* Product table                                                      */
  /* ------------------------------------------------------------------ */
  productTable: {
    articleNo: 'N° d\'article',
    name: 'Nom',
    partner: 'Partenaire',
    indication: 'Indication',
    application: 'Application',
    country: 'Pays',
    dailyDose: 'Dose journalière',
    claims: 'Allégations',
    actions: 'Actions',
  },

  /* ------------------------------------------------------------------ */
  /* Product detail                                                     */
  /* ------------------------------------------------------------------ */
  productDetail: {
    tabs: {
      overview: 'Aperçu',
      productData: 'Données produit',
      moreDetails: 'Prix',
    },
    notFound: 'Produit introuvable',
    backToOverview: 'Retour à l\'aperçu',
    marketingClaims: 'Allégations marketing',
    indications: 'Indications',
    applications: 'Applications',
    countries: 'Pays',
    description: 'Description',
    shortDescription: 'Description courte',
    website: 'Site web du produit',
    multimedia: 'Site web multimédia',
    studies: 'Études',
    origin: 'Origine',
    dosage: 'Dosage',
    appearance: 'Apparence',
    category: 'Catégorie',
    articleNumber: 'Numéro d\'article',
    active: 'Actif',
    created: 'Créé',
    showDailyPrice: 'Afficher le prix journalier',
    perDay: '/ jour',
    generatingPdf: 'Génération du PDF...',
    sendViaEmail: 'Envoyer par e-mail',
    emailSubject: (name: string, companyName: string) => `${companyName} — Fiche produit\u00a0: ${name}`,
    emailBody: (name: string, userName: string, companyName: string) =>
      `Cher client,\n\nVeuillez trouver ci-joint la fiche produit pour ${name}.\n\nN'hésitez pas à me contacter si vous avez des questions ou besoin d'informations supplémentaires.\n\nCordialement,\n${userName}\n${companyName}`,
    toastEmail: 'E-mail envoyé avec succès\u00a0!',
  },

  /* ------------------------------------------------------------------ */
  /* Actions (favorite, cart, compare)                                   */
  /* ------------------------------------------------------------------ */
  actions: {
    addToFavorites: 'Ajouter aux favoris',
    removeFromFavorites: 'Retirer des favoris',
    favorited: 'Mis en favori',
    favorite: 'Favori',
    addToCart: 'Ajouter au document',
    removeFromCart: 'Retirer du document',
    removeFromAllCarts: 'Retirer de tous les documents',
    inCart: 'Dans le document',
    addToCartLabel: 'Ajouter au document',
    inCartLabel: 'Dans le document',
    cartLabel: 'Documents',
    addToComparison: 'Ajouter à la comparaison',
    removeFromComparison: 'Retirer de la comparaison',
    inComparison: 'Dans la comparaison',
    compared: 'Comparé',
    compare: 'Comparer',
    manageFavorites: 'Gérer les favoris',
    addToFavoritesNoGroup: 'Ajouter aux favoris (sans groupe)',
    favoritesLabel: 'Favoris',
  },

  /* ------------------------------------------------------------------ */
  /* Favorites page                                                     */
  /* ------------------------------------------------------------------ */
  favorites: {
    title: 'Favoris',
    noFavorites: 'Aucun favori',
    noFavoritesDescription: 'Marquez des produits comme favoris pour les voir ici.',
    deleteGroup: {
      title: 'Supprimer le groupe',
      message: (name: string) => `Êtes-vous sûr de vouloir supprimer « ${name} » ?`,
      moveLabel: 'Déplacer les produits vers :',
      ungroupedOption: 'Pas dans un groupe (non groupé)',
      confirm: 'Supprimer le groupe',
      cancel: 'Annuler',
    },
    browseProducts: 'Parcourir les produits',
    dragProductsHint: 'Glissez les produits ici',
    notInGroup: 'Pas dans un groupe',
    notInGroupWithCount: (count: number) => `Pas dans un groupe (${count})`,
    dragCopyHint: '⌘/⇧ maintenu — relâchez pour copier dans le groupe',
    dragMoveHint: 'Relâchez pour déplacer · maintenez ⌘/⇧ pour copier',
    addGroupToDocument: 'Ajouter le groupe au document',
    deleteGroupTitle: 'Supprimer le groupe',
    groupNamePlaceholder: 'Nom du groupe...',
    create: 'Créer',
    cancel: 'Annuler',
    newGroup: 'Nouveau groupe',
    inDocument: 'Dans le document',
    alreadyInDocument: 'Les produits sont déjà dans le document.',
    addedToDocument: 'Produits ajoutés au document.',
    removedFromDocument: 'Produits retirés du document.',
    addToDocument: 'Ajouter',
    productCountLabel: (count: number) => `${count} ${count === 1 ? 'produit' : 'produits'}`,
    openProductTitle: 'Ouvrir',
  },

  /* ------------------------------------------------------------------ */
  /* Comparison page                                                    */
  /* ------------------------------------------------------------------ */
  comparison: {
    title: 'Comparaison de produits',
    loading: 'Chargement des produits...',
    productCount: (count: number, max: number) => `(${count}/${max})`,
    noProducts: 'Aucun produit à comparer',
    noProductsDescription: 'Ajoutez des produits pour les comparer côte à côte.',
    browseProducts: 'Parcourir les produits',
    addProduct: 'Ajouter un produit',
    removeFromComparison: 'Retirer de la comparaison',
    removeAll: 'Tout retirer',
    showDifferencesOnly: 'Afficher uniquement les différences',
    showingDifferencesOnly: 'Affichage des différences uniquement',
    hideDailyPrice: 'Masquer le prix journalier',
    dailyPriceHidden: 'Prix journalier masqué',
    hidePrice: 'Masquer le prix',
    priceHidden: 'Prix masqué',
    allIdentical: 'Toutes les valeurs sont identiques pour ces produits.',
    rows: {
      articleName: 'Nom d\'article',
      articleNumber: 'Numéro d\'article',
      partner: 'Partenaire',
      manufacturer: 'Fabricant',
      category: 'Catégorie',
      clinicalStudies: 'Études cliniques',
      bioactiveIngredients: 'Ingrédients bioactifs / Dosage',
      indications: 'Indications',
      applications: 'Applications',
      countries: 'Pays',
      dosage: 'Dosage',
      dailyDosage: 'Dose journalière',
      appearance: 'Apparence',
      origin: 'Origine',
      sourceOrigin: 'Source / Origine',
      marketingClaims: 'Allégations marketing',
      dailyPrice: 'Prix journalier',
      price: 'Prix',
      novelFood: 'Novel Food',
    },
    novelFoodStatuses: {
      yes: 'Oui',
      ongoing: 'En cours',
      approved: 'Approuvé',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Cart page                                                          */
  /* ------------------------------------------------------------------ */
  cart: {
    title: 'Document de réunion',
    subtitle: 'Produits pour votre réunion client',
    emptyTitle: 'Votre document est vide',
    emptyDescription: 'Ajoutez des produits depuis le catalogue.',
    moveUp: 'Déplacer vers le haut',
    moveDown: 'Déplacer vers le bas',
    remove: 'Supprimer',
    rename: 'Renommer',
    lockedNotice: (date: string) => `Ce document a été envoyé le ${date} et ne peut plus être modifié.`,
    generatePdf: 'Générer le PDF',
    generatingPdf: 'Génération du PDF...',
    sendViaEmail: 'Envoyer par e-mail',
    createOpportunity: 'Créer une opportunité dans SalesForce',
    clearCart: 'Vider le document',
    archiveDocument: 'Archiver le document',
    confirmArchive: (name: string) => `Archiver « ${name} » ? Il n'apparaîtra plus dans votre liste de documents et ne sera ensuite visible que par les administrateurs.`,
    deleteDocument: 'Supprimer le document',
    confirmClear: 'Êtes-vous sûr de vouloir vider le document\u00a0?',
    confirmDelete: (name: string) => `Supprimer « ${name} » ? Le document et tous ses produits seront supprimés.`,
    toastPdf: 'Le PDF a été généré et téléchargé.',
    toastPdfError: 'La génération du PDF a échoué.',
    toastEmail: 'E-mail envoyé avec succès\u00a0!',
    showDocuments: 'Afficher les documents',
    newDocument: 'Nouveau document',
    searchDocuments: 'Rechercher des documents...',
    noDocumentsFound: 'Aucun document trouvé.',
    items: 'éléments',
    comingSoon: 'Bientôt disponible',
    comingSoonDescription: 'L\'intégration SalesForce est en cours de développement. Cette fonctionnalité sera disponible dans une prochaine mise à jour.',
    productCount: (count: number) => `${count} ${count === 1 ? 'produit' : 'produits'}`,
    emailSubject: (count: number, companyName: string) => `${companyName} — Sélection de produits (${count} produits)`,
    emailBody: (productList: string, userName: string, companyName: string) => `Cher client,\n\nVeuillez trouver ci-dessous la sélection de produits de notre dernière réunion :\n\n${productList}\n\nN'hésitez pas à me contacter si vous avez des questions ou besoin d'informations supplémentaires sur l'un de ces produits.\n\nCordialement,\n${userName}\n${companyName}`,
    email: {
      title: 'Envoyer par e-mail',
      recipients: 'Destinataires',
      subject: 'Objet',
      message: 'Message',
      placeholder: 'Saisir une adresse e-mail...',
      cancel: 'Annuler',
      send: 'Envoyer l\'e-mail',
      sending: 'Envoi en cours...',
      invalidEmail: 'Veuillez saisir une adresse e-mail valide.',
      duplicateEmail: 'Cette adresse e-mail a déjà été ajoutée.',
      sendError: 'L\'envoi de l\'e-mail a échoué. Veuillez réessayer.',
      products: 'Produits',
      productsCount: (selected: number, total: number) => `${selected} sur ${total}`,
      selectAll: 'Tout sélectionner',
      deselectAll: 'Tout désélectionner',
      noProductsWarning: 'Sélectionnez au moins un produit à inclure dans l\'e-mail.',
      fileTypesLegend: 'Joindre des documents',
      fileTypesLoading: 'Chargement des documents disponibles…',
      fileTypes: {
        study: 'Études',
        marketing: 'Documents marketing',
        quality: 'Documents qualité',
      },
      qualityHint: 'Les documents qualité ne sont pas joints. {email} les envoie séparément aux destinataires.',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Settings                                                           */
  /* ------------------------------------------------------------------ */
  settings: {
    title: 'Paramètres',
    subtitle: 'Configurez les préférences de votre application',
    multiCart: {
      title: 'Documents multiples',
      description:
        'Activez plusieurs documents de réunion pour organiser les produits selon différents clients ou réunions. Vous pouvez avoir jusqu\'à 3 documents et basculer entre eux depuis l\'en-tête.',
    },
    favoritesOnHome: {
      title: 'Favoris sur le tableau de bord',
      description: 'Affichez votre section de favoris sur l\'écran d\'accueil pour un accès rapide aux produits sauvegardés.',
    },
    favoriteGroups: {
      title: 'Groupes de favoris',
      description: 'Organisez vos favoris en groupes. Vous pouvez créer jusqu\'à 5 groupes et faire glisser des produits entre eux. Chaque groupe peut être ajouté à un document en un clic.',
    },
    productData: {
      title: 'Données produits',
      description: 'Indique quand les données produits ont été chargées pour la dernière fois depuis le serveur.',
      lastLoaded: 'Dernier chargement',
      never: 'Jamais chargé',
      reload: 'Recharger les produits',
      reloading: 'Rechargement...',
      reloadSuccess: 'Données produits rechargées',
      reloadError: 'Impossible de recharger les données produits',
    },
    changePassword: {
      title: 'Changer le mot de passe',
      description: 'Mettez à jour le mot de passe que vous utilisez pour vous connecter.',
      currentPassword: 'Mot de passe actuel',
      newPassword: 'Nouveau mot de passe',
      confirmPassword: 'Confirmer le nouveau mot de passe',
      submit: 'Mettre à jour le mot de passe',
      submitting: 'Mise à jour...',
      success: 'Votre mot de passe a été mis à jour.',
      passwordMismatch: 'Les mots de passe ne correspondent pas',
      error: 'Impossible de mettre à jour le mot de passe',
      backToSettings: 'Retour aux paramètres',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Wizard                                                             */
  /* ------------------------------------------------------------------ */
  wizard: {
    steps: {
      country: 'Pays',
      application: 'Application',
      indication: 'Indication',
    },
    selectCountry: 'Sélectionner un pays',
    selectCountrySubtitle: 'Dans quels pays le produit doit-il être disponible\u00a0?',
    selectApplication: 'Sélectionner une application',
    selectApplicationSubtitle: 'Quelle forme galénique préférez-vous\u00a0?',
    selectIndication: 'Sélectionner une indication',
    selectIndicationSubtitle: 'Quels domaines de santé sont pertinents\u00a0?',
    showResults: 'Afficher les résultats',
    noSelection: 'Aucune sélection effectuée',
    selected: (count: number) => `${count} sélectionné(s)`,
    filterAnd: 'Et',
    filterOr: 'Ou',
    filterAndExplanation: 'Les produits doivent correspondre à tous les critères sélectionnés',
    filterOrExplanation: 'Les produits doivent correspondre à au moins un critère par étape',
  },

  /* ------------------------------------------------------------------ */
  /* Filter sidebar                                                     */
  /* ------------------------------------------------------------------ */
  filterSidebar: {
    title: 'Filtrer',
    category: 'Catégorie',
    country: 'Pays',
    application: 'Application',
    indication: 'Indication',
    partner: 'Partenaire',
    marketingClaims: 'Allégations marketing',
    agreement: 'Accord',
    agreements: {
      DA: 'DA — Contrat de distribution',
      CPA: 'CPA — Protection client',
      Gentleman: 'Gentleman Agreement',
      LOI: 'LOI — Lettre d’intention',
      none: 'Aucun accord',
    },
    agreementDescriptions: {
      DA: 'Contrat de distribution (exclusif)',
      CPA: 'Accord de protection client (non exclusif)',
      Gentleman: 'Gentleman Agreement (verbal, sans contrat)',
      LOI: 'Lettre d’intention (étape préliminaire du DA)',
      none: 'Aucun accord signé pour ce pays',
    },
    newProducts: 'Nouveaux produits',
    newStudy: 'Nouvelle étude',
    restrictedTo: (territories: string) => `Votre vue est limitée à ${territories}.`,
  },

  /* ------------------------------------------------------------------ */
  /* Marketing claim descriptions                                       */
  /* ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------ */
  /* Not Found (404)                                                    */
  /* ------------------------------------------------------------------ */
  notFound: {
    title: '404',
    heading: 'Page introuvable',
    description: 'La page que vous recherchez n\'existe pas ou a été déplacée.',
    goBack: 'Retour',
    home: 'Accueil',
  },

  /* ------------------------------------------------------------------ */
  /* Forgot Password                                                    */
  /* ------------------------------------------------------------------ */
  forgotPassword: {
    subtitle: 'Réinitialisez votre mot de passe',
    email: 'E-mail',
    emailPlaceholder: 'votre@email.com',
    submit: 'Envoyer le lien de réinitialisation',
    submitting: 'Envoi en cours...',
    success: 'Nous avons envoyé un lien de réinitialisation du mot de passe à votre adresse e-mail.',
    backToLogin: 'Retour à la connexion',
    error: 'Impossible d\'envoyer le lien de réinitialisation',
  },

  /* ------------------------------------------------------------------ */
  /* Reset Password                                                     */
  /* ------------------------------------------------------------------ */
  resetPassword: {
    subtitle: 'Définissez un nouveau mot de passe',
    email: 'E-mail',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmer le mot de passe',
    submit: 'Réinitialiser le mot de passe',
    submitting: 'Réinitialisation en cours...',
    passwordMismatch: 'Les mots de passe ne correspondent pas',
    backToLogin: 'Retour à la connexion',
    error: 'Impossible de réinitialiser le mot de passe',
  },

  /* ------------------------------------------------------------------ */
  /* Marketing claim descriptions                                       */
  /* ------------------------------------------------------------------ */
  claimDescriptions: {
    Vegan: 'Ne contient aucun ingrédient ni sous-produit d\'origine animale.',
    Organic: 'Certifié biologique — cultivé sans pesticides ni engrais de synthèse.',
    'Non-GMO': 'Sans organisme génétiquement modifié.',
    'Clinically Studied': 'Efficacité soutenue par des essais cliniques publiés.',
    Patented: 'Protégé par un ou plusieurs brevets pour une formulation ou un procédé unique.',
    Halal: 'Certifié Halal — conforme aux directives alimentaires islamiques.',
    Kosher: 'Certifié Casher — conforme aux lois alimentaires juives.',
    'Gluten-Free': 'Ne contient pas de gluten — adapté aux personnes atteintes de maladie cœliaque ou d\'intolérance au gluten.',
  },
  pdf: {
    productDataSheet: 'Fiche produit',
    productDataSheets: 'Fiches produits',
    keyFacts: 'Donnees cles',
    dailyDosage: 'Dose journaliere',
    appearance: 'Apparence',
    bioactiveIngredient: 'Ingredient bioactif',
    indications: 'Indications',
    possibleApplications: 'Applications possibles',
    description: 'Description',
    linkStudies: 'Etudes',
    origin: 'Origine',
    confidential: 'Confidentiel -- Usage professionnel uniquement',
    productCountOne: ':count produit',
    productCountOther: ':count produits',
    clinicalStudies: 'Etudes cliniques',
    marketingClaims: 'Allegations marketing',
    territories: 'Territoires',
    moq: 'Quantite min. de commande en kg',
    shelfLife: 'Duree de conservation',
    shelfLifeValue: ':months mois',
    novelFoodStatus: 'Novel Food',
    badgeNew: 'NOUVEAU',
    badgeNewStudy: 'NOUVELLE ETUDE',
    contactPerson: 'Votre contact chez Vivatis:',
    website: 'Site web du produit',
    multimedia: 'Site web multimédia',
  },
} as const;

export default fr;
